import AddRoad from '@mui/icons-material/AddRoad';
import Link from '@mui/icons-material/Link';
import LocalShipping from '@mui/icons-material/LocalShipping';
import Smartphone from '@mui/icons-material/Smartphone';
import TireRepair from '@mui/icons-material/TireRepair';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { SxProps } from '@mui/system';
import { LocationHistorySource } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';

import { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { formatSpeed } from '~utils/speed';

import { headerCellSx } from './geofenceTableStyles';

/**
 * Location-history source glyphs: the driver's own device gets a phone icon
 * (aligned with the ticket convention), while third-party integrations each get
 * a distinct icon — a tire (Axle), a chain link (Catena), a road (Paver
 * Tracker), and a truck (Command Alkon). Axle is decommissioned but its glyph stays: historical pings keep
 * the source value forever, so the enum retains it.
 */
const LOCATION_SOURCE_ICONS: Record<LocationHistorySource, React.ReactNode> = {
  [LocationHistorySource.DRIVER_APP]: <Smartphone fontSize="inherit" />,
  [LocationHistorySource.AXLE]: <TireRepair fontSize="inherit" />,
  [LocationHistorySource.CATENA]: <Link fontSize="inherit" />,
  [LocationHistorySource.PAVER_TRACKER]: <AddRoad fontSize="inherit" />,
  [LocationHistorySource.COMMAND_ALKON]: <LocalShipping fontSize="inherit" />,
};

/**
 * Source-cell glyph box: a fixed-width, centered inline box so the icon and the
 * em-dash fallback share one anchor and line up horizontally down the column.
 * Icons inherit the cell's cramped 0.8125rem font, so bump them to ~text height.
 */
const sourceCellGlyphSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  verticalAlign: 'middle',
  width: '1.25rem',
  lineHeight: 1,
  '& .MuiSvgIcon-root': { fontSize: '1rem' },
};

interface LocationHistoriesTableProps {
  pings: LocationHistoryMinimal[];
  isFeet: boolean;
  onHoverPing: (location: { lat: string; lon: string } | null) => void;
}

export function LocationHistoriesTable({
  pings,
  isFeet,
  onHoverPing,
}: LocationHistoriesTableProps) {
  return (
    <TableContainer
      component={Paper}
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        fontFamily: 'monospace',
        '& .MuiTableCell-root': {
          fontFamily: 'inherit',
          paddingTop: 1,
          paddingBottom: 1,
          lineHeight: 1.2,
          fontSize: '0.8125rem',
          whiteSpace: 'nowrap',
        },
        '& .MuiTableRow-root': {
          '& td, & th': { borderBottomWidth: 1 },
        },
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>
              {t('admin.job_geo_debug.column_time')}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t('admin.job_geo_debug.column_lat_lon')}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t('admin.job_geo_debug.column_accuracy')}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t('admin.job_geo_debug.column_speed')}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t('admin.job_geo_debug.column_offline_mode')}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t('admin.job_geo_debug.column_source')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pings.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={6}
                align="center"
                sx={{ py: 0.5, color: 'text.secondary' }}
              >
                {t('admin.job_geo_debug.no_location_histories')}
              </TableCell>
            </TableRow>
          ) : (
            pings.map((ping, index) => (
              <TableRow
                key={ping.id}
                onMouseEnter={() => onHoverPing({ lat: ping.lat, lon: ping.lon })}
                onMouseLeave={() => onHoverPing(null)}
                sx={{
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                  backgroundColor: (theme) =>
                    index % 2 === 1 ? theme.semantic.surfaceSunken : 'transparent',
                }}
              >
                <TableCell>
                  {dayjs.tz(ping.occurredAt).format('MMM DD, YYYY HH:mm:ss Z')}
                </TableCell>
                <TableCell>{`${ping.lat}, ${ping.lon}`}</TableCell>
                <TableCell>{ping.accuracy ?? '—'}</TableCell>
                <TableCell>
                  {ping.speed === null
                    ? '—'
                    : formatSpeed(parseFloat(ping.speed), !isFeet)}
                </TableCell>
                <TableCell>
                  {ping.offlineMode
                    ? t('admin.job_geo_debug.yes')
                    : t('admin.job_geo_debug.no')}
                </TableCell>
                <TableCell>
                  {ping.source ? (
                    <Tooltip title={ping.source} placement="top">
                      <Box
                        sx={sourceCellGlyphSx}
                        data-test-id={`location-source-${ping.source}`}
                      >
                        {LOCATION_SOURCE_ICONS[ping.source] ?? ping.source}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Box
                      sx={{ ...sourceCellGlyphSx, color: 'text.secondary' }}
                      data-test-id="location-source-none"
                    >
                      —
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
